const LOG_PREFIX = '[StartupPerf]';
const STARTUP_LOGGING_TIMEOUT_MS = 60_000;

/** Set to true to emit [StartupPerf] console logs during development. */
export const STARTUP_PERF_LOGGING_ENABLED = false;

declare global {
  interface Window {
    __STARTUP_HTML_MS?: number;
  }
}

let originMs: number | null = null;
let startupLoggingActive = STARTUP_PERF_LOGGING_ENABLED;
let startupCompleteMarked = false;
const timers = new Map<string, number>();
let loggingTimeoutId: ReturnType<typeof setTimeout> | null = null;
const startupCompleteListeners = new Set<
  (summary?: Record<string, unknown>) => void
>();

function getOriginMs(): number {
  if (originMs === null) {
    const htmlMs = window.__STARTUP_HTML_MS;
    originMs =
      typeof htmlMs === 'number' && Number.isFinite(htmlMs)
        ? htmlMs
        : performance.now();
  }
  return originMs;
}

function elapsedMs(): number {
  return Math.round(performance.now() - getOriginMs());
}

function scheduleLoggingTimeout(): void {
  if (!STARTUP_PERF_LOGGING_ENABLED) return;
  if (loggingTimeoutId !== null) return;
  loggingTimeoutId = setTimeout(() => {
    if (!startupCompleteMarked) {
      markStartup('loggingWindowExpired', {
        timeoutMs: STARTUP_LOGGING_TIMEOUT_MS,
      });
      startupLoggingActive = false;
    }
  }, STARTUP_LOGGING_TIMEOUT_MS);
}

export function isStartupLoggingActive(): boolean {
  return startupLoggingActive;
}

export function markStartup(
  phase: string,
  detail?: Record<string, unknown>,
): void {
  if (!STARTUP_PERF_LOGGING_ENABLED) return;
  scheduleLoggingTimeout();
  const payload = detail ? ` ${JSON.stringify(detail)}` : '';
  console.log(`${LOG_PREFIX} +${elapsedMs()}ms ${phase}${payload}`);
}

export function startStartupTimer(label: string): void {
  if (!STARTUP_PERF_LOGGING_ENABLED) return;
  timers.set(label, performance.now());
}

export function endStartupTimer(
  label: string,
  detail?: Record<string, unknown>,
): void {
  const startedAt = timers.get(label);
  const durationMs =
    startedAt !== undefined
      ? Math.round(performance.now() - startedAt)
      : undefined;
  timers.delete(label);
  markStartup(label, { ...detail, durationMs });
}

export function logStartupNavigationTiming(): void {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) {
    return;
  }

  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;

  if (!nav) {
    return;
  }

  markStartup('navigationTiming', {
    domInteractiveMs: Math.round(nav.domInteractive),
    domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd),
    loadEventEndMs: Math.round(nav.loadEventEnd),
    responseEndMs: Math.round(nav.responseEnd),
    transferSize: nav.transferSize,
    encodedBodySize: nav.encodedBodySize,
  });
}

export function onStartupComplete(
  listener: (summary?: Record<string, unknown>) => void,
): () => void {
  if (startupCompleteMarked) {
    listener();
    return () => {};
  }
  startupCompleteListeners.add(listener);
  return () => {
    startupCompleteListeners.delete(listener);
  };
}

export function markStartupComplete(summary?: Record<string, unknown>): void {
  if (startupCompleteMarked) {
    return;
  }
  startupCompleteMarked = true;
  startupLoggingActive = false;
  if (loggingTimeoutId !== null) {
    clearTimeout(loggingTimeoutId);
    loggingTimeoutId = null;
  }
  if (STARTUP_PERF_LOGGING_ENABLED) {
    markStartup('startupComplete', summary);
  }
  startupCompleteListeners.forEach((listener) => listener(summary));
  startupCompleteListeners.clear();
}

export function getStartupRequestUrlLabel(
  args: string | { url?: string },
): string {
  if (typeof args === 'string') {
    return args;
  }
  return args.url ?? 'unknown';
}
